import type { DuplicateGroup } from '../types';

/**
 * Las decisiones de duplicados se guardan keyed por ÍNDICE dentro de
 * `analysis.duplicateGroups`. Un batch aditivo puede insertar grupos nuevos
 * (p. ej. un grupo de categorías nuevo desplaza los índices de todos los grupos
 * de productos), dejando las decisiones previas apuntando al grupo equivocado.
 * Este helper re-mapea cada decisión a su nuevo índice localizando el grupo por
 * identidad estable (`type` + `representativeId`).
 */

export type DuplicateDecisionMap = Record<number, 'keep_representative' | 'keep_all'>;

/**
 * Re-mapea decisiones de duplicados (por índice) de una lista de grupos previa
 * a una nueva. Decisiones cuyo grupo ya no existe se descartan.
 *
 * @param {DuplicateGroup[]} previousGroups Grupos sobre los que se tomaron las decisiones.
 * @param {DuplicateGroup[]} nextGroups Grupos del análisis actualizado (post-append).
 * @param {DuplicateDecisionMap} decisions Decisiones keyed por índice de `previousGroups`.
 * @returns {DuplicateDecisionMap} Decisiones keyed por índice de `nextGroups`.
 */
export const remapDuplicateDecisions = (
  previousGroups: DuplicateGroup[],
  nextGroups: DuplicateGroup[],
  decisions: DuplicateDecisionMap,
): DuplicateDecisionMap => {
  const next: DuplicateDecisionMap = {};
  for (const [indexStr, decision] of Object.entries(decisions)) {
    const oldGroup = previousGroups[Number(indexStr)];
    if (!oldGroup) continue;
    const newIndex = nextGroups.findIndex(
      (g) => g.type === oldGroup.type && g.representativeId === oldGroup.representativeId,
    );
    if (newIndex === -1) continue;
    next[newIndex] = decision;
  }
  return next;
};
